// Pure helpers backing the Uploader's file queue. Kept free of React / Chakra
// so the merge / filter / format logic can be unit-tested in isolation (the
// drag-and-drop DOM wiring around them is verified by running the app).

// Merge newly-added files (from the browse dialog or a drag-and-drop) into the
// files already queued in the modal, de-duplicating by name+size so dropping
// the same file twice — or dropping then browsing the same file — never queues
// it twice. Existing order is preserved; new arrivals are appended.
export function mergeUploadFiles(existing, incoming) {
  const merged = [...existing];
  const seen = new Set(existing.map((file) => `${file.name}:${file.size}`));
  for (const file of Array.from(incoming || [])) {
    const key = `${file.name}:${file.size}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(file);
    }
  }
  return merged;
}

// Split a set of files into those whose extension is in `allowedExts` and those
// that aren't. Drag-and-drop bypasses the file input's `accept` filter, so we
// enforce the same allow-list here to keep drop and browse consistent.
export function filterAllowed(files, allowedExts) {
  const set = new Set(allowedExts.map((ext) => ext.toLowerCase()));
  const accepted = [];
  const rejected = [];
  for (const file of Array.from(files || [])) {
    const parts = file.name.split(".");
    const ext = parts.length > 1 ? parts.pop().toLowerCase() : "";
    (ext && set.has(ext) ? accepted : rejected).push(file);
  }
  return { accepted, rejected };
}

// Compact human-readable file size. Returns "" for missing/invalid sizes so the
// caller can simply omit the label.
export function formatBytes(size) {
  if (typeof size !== "number" || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  const units = ["KB", "MB", "GB"];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}
